import EChartCustomized, {
    SCHEMES,
    TOOLTIP_OPTIONS,
} from '@/components/EchartCustomized'
import { formatVolumeUnit } from '@/utils/function'
import dayjs from 'dayjs'
import React from 'react'
import { Typogaphy } from 'vnetwork-ui'
import { Volume } from '../../types'

interface VolumeChartProps {
    volume: Volume
}

const VolumeChart: React.FC<VolumeChartProps> = ({ volume }) => {
    const totalVolumes = volume?.data.reduce((volume, viewer) => {
        return volume + (viewer?.value || 0)
    }, 0)
    const timestamps = volume?.data.map((item) =>
        dayjs(item.timestamp)?.format('YYYY-MM-DD')
    )

    // Chốt MỘT đơn vị chung cho trục Y theo giá trị đỉnh, thay vì để mỗi tick tự
    // scale (dễ lệch đơn vị: đáy "500 TB" nhưng đỉnh "1 PB" → trục nhìn sai).
    const VOLUME_UNITS = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB']
    const maxVolume = Math.max(
        0,
        ...(volume?.data?.map((item) => item?.value || 0) ?? [0])
    )
    let yUnitIdx = 0
    let ref = maxVolume
    while (ref >= 1000 && yUnitIdx < VOLUME_UNITS.length - 1) {
        ref = ref / 1000
        ++yUnitIdx
    }
    const yDivisor = Math.pow(1000, yUnitIdx)
    const yUnit = VOLUME_UNITS[yUnitIdx]
    const formatYAxis = (value: number) =>
        `${new Intl.NumberFormat('en-US', {
            maximumFractionDigits: 1,
        }).format((value || 0) / yDivisor)} ${yUnit}`

    const options = {
        tooltip: {
            trigger: 'axis',
            ...TOOLTIP_OPTIONS,
            formatter: function (params: any) {
                const [current] = params

                return `
                <b>${dayjs(current?.name)?.format('DD MMM YYYY')}</b> ${current?.value !== 0 ? formatVolumeUnit(current?.value) : '-'}<br/>
            `
            },
        },
        grid: {
            top: '15%',
            left: 8,
            right: '2%',
            bottom: '0%',
            containLabel: true,
        },
        xAxis: {
            type: 'category',
            boundaryGap: false,
            data: timestamps,
            axisLine: {
                lineStyle: {
                    type: [3, 3],
                    dashOffset: 3,
                },
            },
            axisLabel: {
                align: 'left',
                formatter: (value: any) => dayjs(value)?.format('DD MMM'),
            },
        },
        yAxis: {
            type: 'value',
            splitNumber: 2,
            splitLine: {
                show: true,
                lineStyle: {
                    color: '#e0e0e0',
                    type: [3, 3],
                    dashOffset: 3,
                },
            },
            axisLabel: {
                align: 'right',
                margin: 8,
                color: 'black',
                formatter: (value: number) => formatYAxis(value),
            },
        },
        color: [SCHEMES.primary],
        series: [
            {
                data: volume?.data?.map((item) => item.value),
                label: {
                    formatter: ({ value }: any) => formatVolumeUnit(value),
                },
                type: 'line',
                animation: false,
                showSymbol: false,
                markPoint: {
                    symbol: 'pin',
                    symbolSize: 60,
                    data: [
                        {
                            type: 'max',
                            name: 'Max Volume',
                            itemStyle: {
                                color: SCHEMES.primary,
                            },
                            label: {
                                formatter: (param: any) =>
                                    formatVolumeUnit(param?.value),
                                color: '#ffffff',
                                fontWeight: 'bold',
                                fontSize: 12,
                                padding: [4, 6],
                            },
                        },
                    ],
                },
            },
        ],
    }
    return (
        <div className="bg-white rounded-[8px] p-4">
            <div className="">
                <Typogaphy size="medium" className="font-semibold text-primary">
                    Volume
                </Typogaphy>
            </div>
            {/* @ts-ignore */}
            <EChartCustomized options={options} />
            <div className="py-4">
                <div className="flex items-center gap-2">
                    <div className="inline-block w-[10px] h-[10px] bg-[#2196f3]" />
                    <Typogaphy>Volume</Typogaphy>
                </div>
            </div>
            <div className="text-center">
                <div className="border-1 bg-gray-100 px-2 py-1 inline-block">
                    <div className="flex gap-1">
                        <Typogaphy size="small" className="text-gray-500">
                            Total Volume:
                        </Typogaphy>
                        <Typogaphy
                            size="small"
                            className="font-medium uppercase"
                        >
                            {formatVolumeUnit(totalVolumes)}
                        </Typogaphy>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default VolumeChart
